import { Component, OnDestroy, OnInit } from '@angular/core';
import { FilterChipDto, PdvEvidencijeClient, SefObavestenjeDto, StrankaMultiSelectItem } from '@kodit/core/data-api';
import { ActionMode, ActionType, SelectMode, TableConfig } from '@kodit/core/shared-ui';
import { AlertService, ConfigService, SharedService } from '@kodit/core/services';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { selectFilterByKey } from '../../../../../../shared-subforms/src/lib/racun/state/filters/filter.selectors';
import {
  clearFilter,
  setFilter,
} from '../../../../../../shared-subforms/src/lib/racun/state/filters/filter.actions';
import {
  IPrimljenoObavestenjeFilter,
  PrimljenoObavestenjeFilterComponent,
} from './primljeno-obavestenje-filter/primljeno-obavestenje-filter.component';
import {
  buildSefObavestenjeChips,
  hasSefObavestenjeActiveFilters,
} from '../sef-obavestenje-chips.helper';

@Component({
  selector: 'kodit-primljeno-obavestenje-table',
  templateUrl: './primljeno-obavestenje-table.component.html',
  styleUrls: ['./primljeno-obavestenje-table.component.scss'],
})
export class PrimljenoObavestenjeTableComponent implements OnInit, OnDestroy {
  private _subs: Subscription = new Subscription();

  tableConfig!: TableConfig;
  items: SefObavestenjeDto[] = [];
  chipItems: FilterChipDto[] = [];
  filters: IPrimljenoObavestenjeFilter = {};
  private _allItems: SefObavestenjeDto[] = [];

  constructor(
    private _client: PdvEvidencijeClient,
    private _configService: ConfigService,
    private _alert: AlertService,
    private _storage: Store,
    private _sharedService: SharedService
  ) {}

  ngOnInit(): void {
    this.setTableConfig();

    this._subs.add(
      this._storage.select(selectFilterByKey('SEF|Primljeno')).subscribe((data) => {
        if (data) {
          const parsed: IPrimljenoObavestenjeFilter = JSON.parse(data);
          this.filters = {
            dateFrom: parsed.dateFrom ? new Date(parsed.dateFrom) : undefined,
            dateTo: parsed.dateTo ? new Date(parsed.dateTo) : undefined,
            searchTerm: parsed.searchTerm,
            stranke: parsed.stranke,
          };
        } else {
          this.filters = {};
        }
        this._load();
      })
    );
  }

  setTableConfig(): void {
    this.tableConfig = new TableConfig({
      isLazy: false,
      selectMode: SelectMode.SINGLE,
      tableHeader: 'Lista primljenih obaveštenja o prethodnom porezu',
      onChipRemove: (chip) => this._handleChipRemove(chip),
      rowActions: [
        {
          mode: ActionMode.MULTI,
          type: ActionType.CUSTOM,
          label: 'Preuzmi PDF',
          hasAccessTooltip: 'Preuzmite PDF obaveštenja',
          noAccessTooltip: 'Nemate ovlašćenja za preuzimanje',
          icon: 'fas fa-download',
          callback: (index: number) => {
            const item = this.items[index];
            if (!item?.id) return;
            this._subs.add(
              this._client.getPrimljenoObavestenjePdf(item.id).subscribe((result) => {
                this._sharedService.downloadBlob(result.data, result.fileName ?? `Obavestenje_${item.id}.pdf`);
              })
            );
          },
          shouldDisplayByCondition: (rowData: SefObavestenjeDto) => !!rowData.id,
        },
      ],
      columns: [
        { field: 'noticeNumber', header: 'Broj obaveštenja', type: 'text' },
        { field: 'relatedDocumentNumber', header: 'Broj dokumenta', type: 'text' },
        { field: 'noticeDateStr', header: 'Datum obaveštenja', type: 'text' },
        { field: 'sender', subField: 'vatRegistrationCode', header: 'PIB davaoca', type: 'text' },
        { field: 'sender', subField: 'name', header: 'Naziv davaoca', type: 'text' },
        { field: 'totalVatAmount', header: 'Iznos', type: 'currency' },
      ],
      advancedFilter: {
        component: PrimljenoObavestenjeFilterComponent,
        data: {} as IPrimljenoObavestenjeFilter,
      },
    });
  }

  private _load(): void {
    this._configService.setIsBusy = true;
    this._subs.add(
      this._client
        .getReceivedSefObavestenja(this.filters.dateFrom, this.filters.dateTo)
        .pipe(finalize(() => { this._configService.setIsBusy = false; }))
        .subscribe((res) => {
          if (res?.succeeded) {
            this._allItems = res.data ?? [];
            this.items = this._applyClientFilter(this._allItems);
            this.chipItems = buildSefObavestenjeChips(this.filters, 'Davalac');
            this.tableConfig.advancedFilter!.data = this.filters;
          } else {
            this._alert.addFailedMsg(res?.messages?.[0] ?? 'Greška pri učitavanju');
          }
        })
    );
  }

  private _applyClientFilter(items: SefObavestenjeDto[]): SefObavestenjeDto[] {
    let result = items;

    const searchTerm = this.filters.searchTerm?.toLowerCase().trim();
    if (searchTerm) {
      result = result.filter(
        (item) =>
          item.noticeNumber?.toLowerCase().includes(searchTerm) ||
          item.relatedDocumentNumber?.toLowerCase().includes(searchTerm)
      );
    }

    if (this.filters.stranke?.length) {
      const pibs = this.filters.stranke
        .map((s: StrankaMultiSelectItem) => s.pib)
        .filter((p): p is string => !!p);
      if (pibs.length) {
        result = result.filter((item) => pibs.includes(item.sender?.vatRegistrationCode ?? ''));
      }
    }

    return result;
  }

  private _handleChipRemove(chip: FilterChipDto): void {
    this.tableConfig.advancedFilter!.data[chip.key!] = chip.defaultValue;
    this._updateFilters();
  }

  private _updateFilters(): void {
    if (hasSefObavestenjeActiveFilters(this.filters)) {
      this._storage.dispatch(setFilter({ key: 'SEF|Primljeno', filter: JSON.stringify(this.filters) }));
    } else {
      this._storage.dispatch(clearFilter({ key: 'SEF|Primljeno' }));
    }
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }
}
